import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button, Divider, Loading, Panel, TimeFrameInput, Toggle, Typography } from "@/components/atoms";
import { ConfigDetail } from "@/components/molecules/ConfigDetail/ConfigDetail";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useLazyGetOffloadingConfigurationQuery, useUpdateOffloadingConfigurationMutation } from "@/redux/apis/addOn/addOnApi";
import { useAppSelector } from "@/redux/hooks";
import { addOnItemSelector } from "@/redux/slices/addOn/selectors";
import { OffloadingConfiguration } from "@/redux/slices/addOn/types";
import { addToast } from "@/utils";
import { OffloadingInfoIcon } from "./helpers";
import { offloadingFormSchema } from "./offloadingSchema";

export const OffloadingEditorInDispatch = () => {
  const navigate = useNavigate();
  const offloading = useAppSelector(addOnItemSelector("offloading"));
  const configuration = offloading?.configuration as OffloadingConfiguration | undefined;
  const [getOffloadingConfiguration, { isFetching }] = useLazyGetOffloadingConfigurationQuery();
  const [updateOffloadingConfiguration, { isLoading: isUpdating }] = useUpdateOffloadingConfigurationMutation();

  const {
    control,
    trigger,
    watch,
    register,
    reset,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm<OffloadingConfiguration>({
    resolver: zodResolver(offloadingFormSchema),
    defaultValues: configuration,
  });

  const { enabledAutomaticDispatchPrivateNetwork, enabledAutomaticDispatchPublicNetwork } = watch();

  useEffect(() => {
    getOffloadingConfiguration();
  }, [getOffloadingConfiguration]);

  useEffect(() => {
    if (configuration) {
      reset(configuration);
    }
  }, [configuration, reset]);

  const onSubmit = handleSubmit((data) => {
    const {
      enabledAutomaticDispatchPrivateNetwork,
      enabledAutomaticDispatchPublicNetwork,
      dispatchAutoPublicNetworkTime,
      dispatchAutoPublicNetworkTimeframe,
      dispatchPrivateNetworkTier1Time,
      dispatchPrivateNetworkTier1Timeframe,
      dispatchPrivateNetworkTier2Time,
      dispatchPrivateNetworkTier2Timeframe,
      dispatchPrivateNetworkTier3Time,
      dispatchPrivateNetworkTier3Timeframe,
    } = data;

    updateOffloadingConfiguration({
      enabled_automatic_dispatch_private_network: enabledAutomaticDispatchPrivateNetwork,
      enabled_automatic_dispatch_public_network: enabledAutomaticDispatchPublicNetwork,
      dispatch_auto_public_network_time: dispatchAutoPublicNetworkTime,
      dispatch_auto_public_network_timeframe: dispatchAutoPublicNetworkTimeframe,
      dispatch_private_network_tier_1_time: dispatchPrivateNetworkTier1Time,
      dispatch_private_network_tier_1_timeframe: dispatchPrivateNetworkTier1Timeframe,
      dispatch_private_network_tier_2_time: dispatchPrivateNetworkTier2Time,
      dispatch_private_network_tier_2_timeframe: dispatchPrivateNetworkTier2Timeframe,
      dispatch_private_network_tier_3_time: dispatchPrivateNetworkTier3Time,
      dispatch_private_network_tier_3_timeframe: dispatchPrivateNetworkTier3Timeframe,
    })
      .unwrap()
      .then(() => {
        addToast("success", "Successfully Updated Offloading");
        navigate("..");
      })
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  });

  if (!offloading) return null;

  return (
    <form onSubmit={onSubmit}>
      <Panel className="mt-5">
        <div className="relative">
          {(isFetching || isUpdating) && <Loading />}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Typography variant="h3" className="leading-8">
                  Offloading
                </Typography>
                <OffloadingInfoIcon />
              </div>
              <Typography className="text-neutral-dark-gray">
                Automatically dispatch jobs to your private or public network when needed.
              </Typography>
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => navigate("..")}>
                Cancel
              </Button>
              <Button type="submit" disabled={!isDirty || isUpdating}>
                Save
              </Button>
            </div>
          </div>

          <div className="my-4 flex w-full flex-col rounded-lg bg-neutral-surface-gray p-5">
            <div className="flex flex-row items-center">
              <div className="flex flex-1 flex-col">
                <Typography variant="action">Auto Dispatch to Private Network</Typography>
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  Set the time before pickup
                </Typography>
              </div>
              <Controller
                name="enabledAutomaticDispatchPrivateNetwork"
                control={control}
                render={({ field }) => (
                  <Toggle
                    checked={field.value}
                    onChange={(checked) => {
                      field.onChange(checked);
                      trigger();
                    }}
                  />
                )}
              />
            </div>
            <ConfigDetail title="Dispatch Tier Times" description="Timing of when to dispatch to each tier." indentPadding="pl-2" />
            <ConfigDetail title="Tier 1" indentPadding="pl-4" isBullet description="Set when the system will start dispatching before pickup">
              <TimeFrameInput
                className="w-48"
                min="0"
                max="999"
                timeName="dispatchPrivateNetworkTier1Time"
                timeFrameName="dispatchPrivateNetworkTier1Timeframe"
                register={register}
                options={["hour", "day"]}
                disabled={!enabledAutomaticDispatchPrivateNetwork}
                error={errors.dispatchPrivateNetworkTier1Time?.message}
              />
            </ConfigDetail>
            <ConfigDetail title="Tier 2" indentPadding="pl-4" isBullet description="Set when the system will start dispatching before pickup">
              <TimeFrameInput
                className="w-48"
                min="0"
                max="999"
                timeName="dispatchPrivateNetworkTier2Time"
                timeFrameName="dispatchPrivateNetworkTier2Timeframe"
                register={register}
                options={["hour", "day"]}
                disabled={!enabledAutomaticDispatchPrivateNetwork}
                error={errors.dispatchPrivateNetworkTier2Time?.message}
              />
            </ConfigDetail>
            <ConfigDetail title="Tier 3" indentPadding="pl-4" isBullet description="Set when the system will start dispatching before pickup">
              <TimeFrameInput
                className="w-48"
                min="0"
                max="999"
                timeName="dispatchPrivateNetworkTier3Time"
                timeFrameName="dispatchPrivateNetworkTier3Timeframe"
                register={register}
                options={["hour", "day"]}
                disabled={!enabledAutomaticDispatchPrivateNetwork}
                error={errors.dispatchPrivateNetworkTier3Time?.message}
              />
            </ConfigDetail>
            <Divider className="my-6" />
            <div className="flex flex-row items-center">
              <div className="flex flex-1 flex-col">
                <Typography variant="action">Auto Dispatch to Public Network</Typography>
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  Set the time before pickup
                </Typography>
              </div>
              <Controller
                name="enabledAutomaticDispatchPublicNetwork"
                control={control}
                render={({ field }) => (
                  <Toggle
                    checked={field.value}
                    onChange={(checked) => {
                      field.onChange(checked);
                      trigger();
                    }}
                  />
                )}
              />
            </div>
            <ConfigDetail title="" description="Set when the system will start dispatching before pickup" indentPadding="pl-2">
              <TimeFrameInput
                className="w-48"
                min="0"
                max="60"
                timeName="dispatchAutoPublicNetworkTime"
                timeFrameName="dispatchAutoPublicNetworkTimeframe"
                register={register}
                options={["hour", "minute", "day"]}
                disabled={!enabledAutomaticDispatchPublicNetwork}
                error={errors.dispatchAutoPublicNetworkTime?.message}
              />
            </ConfigDetail>
          </div>
        </div>
      </Panel>
    </form>
  );
};
